<template>
  <div class="project-calculation-advanced">
    <b-row>
      <b-col xl="6" lg="6" md="12">
        <b-form-group
          horizontal
          :label="$t('costCalculationBasis')"
          label-for="costCalculationBasis"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('costCalculationBasis')"
            v-model="project.costCalculationBasis"
            id="costCalculationBasis"
            :options="getEnumValues('CostCalculationBasis')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedExternalCost')"
          label-for="calculatedExternalCost"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            v-model="project.calculatedExternalCost"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('calculatedExternalCost')"
            :disabled="project.costCalculationBasis === 'ONLY_INTERNAL_COST'"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('projectRightsCost')"
          label-for="projectRightsCost"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="projectRightsCost"
            v-model="project.projectRightsCost"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
            :placeholder="$t('projectRightsCost')"
            :disabled="isOnlyExternalCostBasis"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('oneTimeLeaseCostDriver')"
          label-for="oneTimeLeaseCostDriver"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="oneTimeLeaseCostDriver"
            v-model="oneTimeLeaseCostDriver"
            class="calculation-mode-select"
            :options="leaseDriverOptions"
            :disabled="isOnlyExternalCostBasis"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>
        </b-form-group>

        <b-form-group
          v-if="isLeasePerSquareMeterDriver"
          horizontal
          :label="$t('oneTimeLeaseCostPerSquareMeter')"
          label-for="oneTimeLeaseCostPerSquareMeter"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="oneTimeLeaseCostPerSquareMeter"
            v-model="project.oneTimeLeaseCostPerSquareMeter"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="4"
            suffix=" EUR/m²"
            locale="de-DE"
            :placeholder="$t('oneTimeLeaseCostPerSquareMeter')"
            :disabled="isOnlyExternalCostBasis"
          />
        </b-form-group>

        <b-form-group
          v-else
          horizontal
          :label="$t('oneTimeLeaseCostPerkWp')"
          label-for="oneTimeLeaseCostPerkWp"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="oneTimeLeaseCostPerkWp"
            v-model="project.oneTimeLeaseCostPerkWp"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="4"
            suffix=" EUR/kWp"
            locale="de-DE"
            :placeholder="$t('oneTimeLeaseCostPerkWp')"
            :disabled="isOnlyExternalCostBasis"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('effectiveProjectLeaseCost')"
          label-for="effectiveProjectLeaseCost"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <div class="form-control-plaintext">
            {{ formatNumber(effectiveProjectLeaseCost, NUMBER_FORMAT.CURRENCY) }}
          </div>
          <small class="text-muted">
            {{ effectiveProjectLeaseCostFormulaHint }}
          </small>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('additionalProfitMargin')"
          label-for="additionalProfitMargin"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            id="additionalProfitMargin"
            v-model="project.additionalProfitMargin"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" %"
            locale="de-DE"
            :placeholder="'%'"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('baseSettlementValuePerkWp')"
          label-for="baseSettlementValuePerkWp"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            v-model="project.baseSettlementValuePerkWp"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="4"
            suffix=" EUR/kWp"
            locale="de-DE"
            :placeholder="$t('baseSettlementValuePerkWp')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('baseSettlementValue')"
          label-for="baseSettlementValueMode"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="baseSettlementValueMode"
            v-model="baseSettlementValueMode"
            class="calculation-mode-select"
            :options="automaticManualOptions"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>

          <InputNumber
            v-if="isBaseSettlementValueManual"
            v-model="displayedBaseSettlementValue"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
          />
          <div v-else class="form-control-plaintext">
            {{ formatNumber(displayedBaseSettlementValue, NUMBER_FORMAT.CURRENCY) }}
          </div>
          <small v-if="!isBaseSettlementValueManual" class="text-muted">
            {{ baseSettlementValueFormulaHint }}
          </small>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('commissionPercentage')"
          label-for="commissionPercentageMode"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="commissionPercentageMode"
            v-model="commissionPercentageMode"
            class="calculation-mode-select"
            :options="automaticManualOptions"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>

          <InputNumber
            v-if="isCommissionPercentageManual"
            v-model="displayedCommissionPercentage"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="5"
            suffix=" %"
            locale="de-DE"
          />
          <div v-else class="form-control-plaintext">
            {{
              formatNumber(displayedCommissionPercentage / 100, NUMBER_FORMAT.PERCENT_FIVE_DECIMALS)
            }}
          </div>
          <small v-if="!isCommissionPercentageManual" class="text-muted">
            {{ commissionPercentageFormulaHint }}
          </small>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedProvision')"
          label-for="calculatedProvisionMode"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="calculatedProvisionMode"
            v-model="calculatedProvisionMode"
            class="calculation-mode-select"
            :options="automaticManualOptions"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>

          <InputNumber
            v-if="isCalculatedProvisionManual"
            v-model="displayedCalculatedProvision"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
          />
          <div v-else class="form-control-plaintext">
            {{ formatNumber(displayedCalculatedProvision, NUMBER_FORMAT.CURRENCY) }}
          </div>
          <small v-if="!isCalculatedProvisionManual" class="text-muted">
            {{ calculatedProvisionFormulaHint }}
          </small>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedSettlementValueDriver')"
          label-for="calculatedSettlementValueDriver"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="calculatedSettlementValueDriver"
            v-model="settlementValueDriver"
            class="calculation-mode-select"
            :options="settlementDriverOptions"
            @input="onSettlementDriverChange"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedSettlementValue')"
          label-for="calculatedSettlementValue"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            v-if="isManualTotalDriver"
            v-model="project.calculatedSettlementValue"
            mode="currency"
            currency="EUR"
            currencyDisplay="code"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            locale="de-DE"
          />
          <div v-else class="form-control-plaintext d-flex align-items-center">
            {{ formatNumber(appliedCalculatedSettlementValue, NUMBER_FORMAT.CURRENCY) }}
          </div>
          <small v-if="isManualPerKwpDriver" class="text-muted">
            {{ settlementValueDerivedFromPerKwpHint }}
          </small>
          <small v-else-if="isAutoDriver" class="text-muted">
            {{ calculatedSettlementValueFormulaHint }}
          </small>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('calculatedSettlementValuePerkWp')"
          label-for="calculatedSettlementValuePerkWp"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <InputNumber
            v-if="isManualPerKwpDriver"
            v-model="project.calculatedSettlementValuePerkWp"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="4"
            suffix=" EUR/kWp"
            locale="de-DE"
          />
          <div v-else class="form-control-plaintext d-flex align-items-center">
            {{ formatNumber(appliedCalculatedSettlementValuePerkWp, NUMBER_FORMAT.CURRENCY) }}
          </div>
          <small v-if="isManualTotalDriver" class="text-muted">
            {{ settlementPerKwpDerivedFromTotalHint }}
          </small>
          <small v-else-if="isAutoDriver" class="text-muted">
            {{ calculatedSettlementValuePerkWpFormulaHint }}
          </small>
        </b-form-group>
      </b-col>

      <b-col xl="6" lg="6" md="12" class="mt-3 mt-lg-0">
        <ProjectCalculationBaseReferencePanel
          class="mb-3"
          :cost-calculation-basis="project.costCalculationBasis"
          :solar-plant-overall-power="solarPlantOverallPower"
          :calculated-external-cost="project.calculatedExternalCost"
          :project-rights-cost="project.projectRightsCost"
          :effective-project-lease-cost="effectiveProjectLeaseCost"
          :effective-total-planned-material-cost="project.effectiveTotalPlannedMaterialCost"
          :effective-total-labor-cost="project.effectiveTotalLaborCost"
          :effective-general-business-costs="project.effectiveGeneralBusinessCosts"
          :effective-general-business-costs-percentage="
            project.effectiveGeneralBusinessCostsPercentage
          "
          :effective-risk-and-profit="project.effectiveRiskAndProfit"
          :effective-profit-margin-percentage="project.effectiveProfitMarginPercentage"
          :additional-profit-margin="project.additionalProfitMargin"
        />
        <!-- <ProjectCalculationReferencePanel
          :settlement-value-driver="settlementValueDriver"
          :base-settlement-value-perk-wp="project.baseSettlementValuePerkWp"
          :displayed-base-settlement-value="displayedBaseSettlementValue"
          :formula-base-settlement-value="formulaBaseSettlementValue"
          :base-settlement-value-difference-to-auto="baseSettlementValueDifferenceToAuto"
          :effective-commission-percent="effectiveCommissionPercent"
          :auto-commission-percentage="autoCommissionPercentage"
          :commission-percentage-difference-to-auto="commissionPercentageDifferenceToAuto"
          :displayed-calculated-provision="displayedCalculatedProvision"
          :auto-calculated-provision="autoCalculatedProvision"
          :calculated-provision-difference-to-auto="calculatedProvisionDifferenceToAuto"
          :applied-calculated-settlement-value="appliedCalculatedSettlementValue"
          :auto-calculated-settlement-value="autoCalculatedSettlementValue"
          :settlement-value-difference-to-auto="settlementValueDifferenceToAuto"
          :applied-calculated-settlement-value-perk-wp="appliedCalculatedSettlementValuePerkWp"
          :auto-calculated-settlement-value-perk-wp="autoCalculatedSettlementValuePerkWp"
          :settlement-per-kwp-difference-to-auto="settlementPerKwpDifferenceToAuto"
        /> -->
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { formatNumber, NUMBER_FORMAT } from '@/helpers/numberFormat';
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import InputNumber from 'primevue/inputnumber';
import SelectButton from 'primevue/selectbutton';
import ProjectCalculationBaseReferencePanel from './ProjectCalculationBaseReferencePanel.vue';
// import ProjectCalculationReferencePanel from './ProjectCalculationReferencePanel.vue';

const SETTLEMENT_DRIVER_AUTO = 'AUTO';
const SETTLEMENT_DRIVER_MANUAL_TOTAL = 'MANUAL_TOTAL';
const SETTLEMENT_DRIVER_MANUAL_PER_KWP = 'MANUAL_PER_KWP';
const LEASE_DRIVER_PER_KWP = 'PER_KWP';
const LEASE_DRIVER_PER_SQUARE_METER = 'PER_SQUARE_METER';
const MODE_AUTOMATIC = 'AUTOMATIC';
const MODE_MANUAL = 'MANUAL';

export default {
  name: 'ProjectCalculationAdvanced',
  components: {
    Multiselect,
    InputNumber,
    SelectButton,
    ProjectCalculationBaseReferencePanel,
    // ProjectCalculationReferencePanel,
  },
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      NUMBER_FORMAT,
      settlementDriverOptions: [
        SETTLEMENT_DRIVER_AUTO,
        SETTLEMENT_DRIVER_MANUAL_TOTAL,
        SETTLEMENT_DRIVER_MANUAL_PER_KWP,
      ],
      leaseDriverOptions: [LEASE_DRIVER_PER_KWP, LEASE_DRIVER_PER_SQUARE_METER],
      automaticManualOptions: [MODE_AUTOMATIC, MODE_MANUAL],
    };
  },
  computed: {
    ...mapGetters(['getEnumValues', 'getSalesSettings']),

    salesSettings() {
      return this.getSalesSettings || {};
    },

    solarPlantOverallPower() {
      return this.project.solarPlant?.solarPlantOverallPower ?? 0;
    },

    areaInSquareMeters() {
      return this.project.investmentDeductionAllowance?.areaInSquareMeters ?? 0;
    },

    isOnlyExternalCostBasis() {
      return this.project.costCalculationBasis === 'ONLY_EXTERNAL_COST';
    },

    oneTimeLeaseCostDriver: {
      get() {
        return this.project.oneTimeLeaseCostDriver || LEASE_DRIVER_PER_KWP;
      },
      set(value) {
        this.project.oneTimeLeaseCostDriver = value;
      },
    },

    isLeasePerKwpDriver() {
      return this.oneTimeLeaseCostDriver === LEASE_DRIVER_PER_KWP;
    },

    isLeasePerSquareMeterDriver() {
      return this.oneTimeLeaseCostDriver === LEASE_DRIVER_PER_SQUARE_METER;
    },

    /**
     * Live preview; mirrors ProjectCalculationService.calculateProjectLeaseCost.
     * project.effectiveProjectLeaseCost from GraphQL updates after save/refetch.
     */
    effectiveProjectLeaseCost() {
      if (this.isOnlyExternalCostBasis) {
        return 0;
      }
      if (this.isLeasePerSquareMeterDriver) {
        const leaseCostPerSquareMeter = this.project.oneTimeLeaseCostPerSquareMeter ?? 0;
        return leaseCostPerSquareMeter * this.areaInSquareMeters;
      }
      const leaseCostPerKwp = this.project.oneTimeLeaseCostPerkWp ?? 0;
      return leaseCostPerKwp * this.solarPlantOverallPower;
    },

    settlementValueDriver: {
      get() {
        return this.project.calculatedSettlementValueDriver || SETTLEMENT_DRIVER_AUTO;
      },
      set(value) {
        this.project.calculatedSettlementValueDriver = value;
      },
    },

    isAutoDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_AUTO;
    },

    isManualTotalDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_MANUAL_TOTAL;
    },

    isManualPerKwpDriver() {
      return this.settlementValueDriver === SETTLEMENT_DRIVER_MANUAL_PER_KWP;
    },

    isBaseSettlementValueManual() {
      return this.baseSettlementValueMode === MODE_MANUAL;
    },

    isCommissionPercentageManual() {
      return this.commissionPercentageMode === MODE_MANUAL;
    },

    isCalculatedProvisionManual() {
      return this.calculatedProvisionMode === MODE_MANUAL;
    },

    baseSettlementValueMode: {
      get() {
        return this.project.baseSettlementValue != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.baseSettlementValue == null) {
            this.project.baseSettlementValue = this.formulaBaseSettlementValue;
          }
        } else {
          this.project.baseSettlementValue = null;
        }
      },
    },

    commissionPercentageMode: {
      get() {
        return this.project.commissionPercentage != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.commissionPercentage == null) {
            this.project.commissionPercentage = this.salesSettings?.totalCommission ?? null;
          }
        } else {
          this.project.commissionPercentage = null;
        }
      },
    },

    calculatedProvisionMode: {
      get() {
        return this.project.calculatedProvision != null ? MODE_MANUAL : MODE_AUTOMATIC;
      },
      set(mode) {
        if (mode === MODE_MANUAL) {
          if (this.project.calculatedProvision == null) {
            this.project.calculatedProvision =
              (this.effectiveBaseSettlementValue * this.effectiveCommissionPercent) / 100 || 0;
          }
        } else {
          this.project.calculatedProvision = null;
        }
      },
    },

    effectiveCommissionPercent() {
      if (this.project.commissionPercentage != null) {
        return this.project.commissionPercentage;
      }
      return this.autoCommissionPercentage;
    },

    formulaBaseSettlementValue() {
      const baseSettlementValuePerkWp = this.project.baseSettlementValuePerkWp ?? 0;
      return this.solarPlantOverallPower * baseSettlementValuePerkWp || 0;
    },

    effectiveBaseSettlementValue() {
      if (this.project.baseSettlementValue != null) {
        return this.project.baseSettlementValue;
      }
      return this.formulaBaseSettlementValue;
    },

    autoCommissionPercentage() {
      return this.salesSettings?.totalCommission ?? 0;
    },

    autoCalculatedProvision() {
      return (this.formulaBaseSettlementValue * this.autoCommissionPercentage) / 100 || 0;
    },

    effectiveCalculatedProvision() {
      if (this.project.calculatedProvision != null) {
        return this.project.calculatedProvision;
      }
      return (this.effectiveBaseSettlementValue * this.effectiveCommissionPercent) / 100 || 0;
    },

    displayedCommissionPercentage: {
      get() {
        return this.effectiveCommissionPercent;
      },
      set(value) {
        if (this.project.commissionPercentage != null) {
          this.project.commissionPercentage = value;
        }
      },
    },

    displayedBaseSettlementValue: {
      get() {
        return this.effectiveBaseSettlementValue;
      },
      set(value) {
        if (this.project.baseSettlementValue != null) {
          this.project.baseSettlementValue = value;
        }
      },
    },

    displayedCalculatedProvision: {
      get() {
        return this.effectiveCalculatedProvision;
      },
      set(value) {
        if (this.project.calculatedProvision != null) {
          this.project.calculatedProvision = value;
        }
      },
    },

    autoCalculatedSettlementValue() {
      return this.formulaBaseSettlementValue + this.autoCalculatedProvision;
    },

    effectiveCalculatedSettlementValue() {
      if (this.isManualTotalDriver) {
        return this.project.calculatedSettlementValue ?? 0;
      }
      if (this.isManualPerKwpDriver) {
        const settlementValuePerkWp = this.project.calculatedSettlementValuePerkWp ?? 0;
        return settlementValuePerkWp * this.solarPlantOverallPower;
      }
      return this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision;
    },

    appliedCalculatedSettlementValue() {
      return this.effectiveCalculatedSettlementValue;
    },

    autoCalculatedSettlementValuePerkWp() {
      if (this.solarPlantOverallPower === 0) {
        return 0;
      }
      const value = this.autoCalculatedSettlementValue / this.solarPlantOverallPower;
      return Math.round(value * 100) / 100;
    },

    effectiveCalculatedSettlementValuePerkWp() {
      if (this.isManualPerKwpDriver) {
        return this.project.calculatedSettlementValuePerkWp ?? 0;
      }
      if (this.solarPlantOverallPower === 0) {
        return 0;
      }
      if (this.isManualTotalDriver) {
        const settlementValue = this.project.calculatedSettlementValue ?? 0;
        return settlementValue / this.solarPlantOverallPower;
      }
      return this.effectiveCalculatedSettlementValue / this.solarPlantOverallPower;
    },

    appliedCalculatedSettlementValuePerkWp() {
      return this.effectiveCalculatedSettlementValuePerkWp;
    },

    settlementValueDifferenceToAuto() {
      return this.effectiveCalculatedSettlementValue - this.autoCalculatedSettlementValue;
    },

    settlementPerKwpDifferenceToAuto() {
      return (
        this.effectiveCalculatedSettlementValuePerkWp - this.autoCalculatedSettlementValuePerkWp
      );
    },

    baseSettlementValueDifferenceToAuto() {
      return this.effectiveBaseSettlementValue - this.formulaBaseSettlementValue;
    },

    commissionPercentageDifferenceToAuto() {
      return this.effectiveCommissionPercent - this.autoCommissionPercentage;
    },

    calculatedProvisionDifferenceToAuto() {
      return this.effectiveCalculatedProvision - this.autoCalculatedProvision;
    },

    effectiveProjectLeaseCostFormulaHint() {
      if (this.isLeasePerSquareMeterDriver) {
        const leasePerSquareMeterFormatted = formatNumber(
          this.project.oneTimeLeaseCostPerSquareMeter ?? 0,
          NUMBER_FORMAT.CURRENCY
        );
        const areaFormatted = `${formatNumber(this.areaInSquareMeters, NUMBER_FORMAT.DECIMAL)} m²`;
        return `${this.$t('oneTimeLeaseCostPerSquareMeter')} × ${this.$t(
          'investmentDeductionAllowance_areaInSquareMeters'
        )} (${leasePerSquareMeterFormatted} × ${areaFormatted})`;
      }

      const leasePerKwpFormatted = `${formatNumber(
        this.project.oneTimeLeaseCostPerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('oneTimeLeaseCostPerkWp')} × ${this.$t(
        'solarPlantOverallPower'
      )} (${leasePerKwpFormatted} × ${powerFormatted})`;
    },

    baseSettlementValueFormulaHint() {
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      const perKwpFormatted = `${formatNumber(
        this.project.baseSettlementValuePerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      return `${this.$t('baseSettlementValue')}: ${this.$t('solarPlantOverallPower')} × ${this.$t(
        'baseSettlementValuePerkWp'
      )} (${powerFormatted} x ${perKwpFormatted})`;
    },

    commissionPercentageFormulaHint() {
      const globalValue = this.salesSettings?.totalCommission;
      if (globalValue != null) {
        return `${this.$t('totalCommission')}: ${formatNumber(
          globalValue / 100,
          NUMBER_FORMAT.PERCENT_FIVE_DECIMALS
        )} aus Einstellungen`;
      }
      return this.$t('totalCommission');
    },

    calculatedProvisionFormulaHint() {
      const baseSettlementValueFormatted = formatNumber(
        this.displayedBaseSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const commissionFormatted = formatNumber(
        this.effectiveCommissionPercent / 100,
        NUMBER_FORMAT.PERCENT
      );
      return `${this.$t('calculatedProvision')}: ${this.$t('baseSettlementValue')} × ${this.$t(
        'commissionPercentage'
      )} (${baseSettlementValueFormatted} x ${commissionFormatted})`;
    },

    calculatedSettlementValueFormulaHint() {
      const baseSettlementValueFormatted = formatNumber(
        this.displayedBaseSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const provisionFormatted = formatNumber(
        this.displayedCalculatedProvision,
        NUMBER_FORMAT.CURRENCY
      );
      return `${this.$t('calculatedSettlementValue')}: ${this.$t(
        'baseSettlementValue'
      )} + ${this.$t(
        'calculatedProvision'
      )} (${baseSettlementValueFormatted} + ${provisionFormatted})`;
    },

    calculatedSettlementValuePerkWpFormulaHint() {
      const settlementValueFormatted = formatNumber(
        this.appliedCalculatedSettlementValue,
        NUMBER_FORMAT.CURRENCY
      );
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValuePerkWp')}: ${this.$t(
        'calculatedSettlementValue'
      )} ÷ ${this.$t('solarPlantOverallPower')} (${settlementValueFormatted} ÷ ${powerFormatted})`;
    },

    settlementValueDerivedFromPerKwpHint() {
      const perKwpFormatted = `${formatNumber(
        this.project.calculatedSettlementValuePerkWp ?? 0,
        NUMBER_FORMAT.CURRENCY
      )}`;
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValue')}: ${this.$t(
        'calculatedSettlementValuePerkWp'
      )} × ${this.$t('solarPlantOverallPower')} (${perKwpFormatted} x ${powerFormatted})`;
    },

    settlementPerKwpDerivedFromTotalHint() {
      const settlementValueFormatted = formatNumber(
        this.project.calculatedSettlementValue ?? 0,
        NUMBER_FORMAT.CURRENCY
      );
      const powerFormatted = `${formatNumber(
        this.solarPlantOverallPower,
        NUMBER_FORMAT.DECIMAL
      )} ${this.$t('solarPlantOverallPower_short')}`;
      return `${this.$t('calculatedSettlementValuePerkWp')}: ${this.$t(
        'calculatedSettlementValue'
      )} ÷ ${this.$t('solarPlantOverallPower')} (${settlementValueFormatted} ÷ ${powerFormatted})`;
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues', 'fetchSettingsByCategory']),
    formatNumber,

    onSettlementDriverChange(newDriver) {
      if (
        newDriver === SETTLEMENT_DRIVER_MANUAL_TOTAL &&
        this.project.calculatedSettlementValue == null
      ) {
        this.project.calculatedSettlementValue =
          this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision;
      }
      if (
        newDriver === SETTLEMENT_DRIVER_MANUAL_PER_KWP &&
        this.project.calculatedSettlementValuePerkWp == null
      ) {
        if (this.solarPlantOverallPower === 0) {
          this.project.calculatedSettlementValuePerkWp = 0;
        } else {
          this.project.calculatedSettlementValuePerkWp =
            (this.effectiveBaseSettlementValue + this.effectiveCalculatedProvision) /
            this.solarPlantOverallPower;
        }
      }
    },

    async setup() {
      await Promise.all([
        this.fetchEnumValues('CostCalculationBasis'),
        this.fetchEnumValues('CalculatedSettlementValueDriver'),
      ]);
    },
  },
  async mounted() {
    await this.fetchSettingsByCategory('SALES');
    if (!this.project.calculatedSettlementValueDriver) {
      this.project.calculatedSettlementValueDriver = SETTLEMENT_DRIVER_AUTO;
    }
    if (!this.project.oneTimeLeaseCostDriver) {
      this.project.oneTimeLeaseCostDriver = LEASE_DRIVER_PER_KWP;
    }
    await this.setup();
  },
};
</script>

<style scoped lang="scss">
::v-deep .calculation-mode-select.p-selectbutton .p-button {
  padding: 0.1rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
</style>
